import React from 'react';

export class GlobalErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { 
            hasError: false, 
            error: null, 
            errorInfo: null,
            copied: false 
        };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true, error };
    }

    componentDidCatch(error, errorInfo) {
        console.error("ErrorBoundary caught an error:", error, errorInfo);
        this.setState({ errorInfo });
    }

    handleCopyReport = () => {
        const report = `
TornadoCRM Error Report
=======================
Timestamp: ${new Date().toISOString()}
Error: ${this.state.error?.toString()}
Stack: ${this.state.error?.stack}
Component Stack: ${this.state.errorInfo?.componentStack}
        `.trim();

        navigator.clipboard.writeText(report);
        this.setState({ copied: true });
        setTimeout(() => this.setState({ copied: false }), 2000);
    };

    render() {
        if (this.state.hasError) {
            return (
                <div className="min-h-screen bg-slate-50 dark:bg-[#0f172a] flex items-center justify-center p-6 font-sans">
                    <div className="max-w-2xl w-full bg-white dark:bg-[#1e293b] rounded-[2.5rem] shadow-2xl border border-slate-200 dark:border-white/10 overflow-hidden animate-in fade-in zoom-in duration-500">
                        
                        {/* Header Decoration */}
                        <div className="h-2 bg-gradient-to-r from-red-500 via-amber-500 to-rose-500"></div>
                        
                        <div className="p-8 md:p-12">
                            <div className="w-20 h-20 bg-red-50 dark:bg-red-500/10 rounded-3xl flex items-center justify-center mb-8 mx-auto">
                                <span className="material-symbols-outlined text-red-500 text-4xl">error</span>
                            </div>

                            <div className="text-center mb-8">
                                <h1 className="text-3xl font-black text-slate-900 dark:text-white tracking-tighter mb-3">
                                    ¡Vaya! Algo salió mal
                                </h1>
                                <p className="text-slate-500 dark:text-slate-400 font-medium">
                                    La aplicación ha encontrado un error inesperado. Hemos registrado los detalles para nuestro equipo técnico.
                                </p>
                            </div>

                            {/* Error Summary */}
                            <div className="bg-slate-50 dark:bg-black/20 rounded-2xl p-6 border border-slate-100 dark:border-white/5 mb-8">
                                <p className="text-xs font-black uppercase tracking-widest text-slate-400 mb-2">Error Detectado</p>
                                <code className="text-sm font-bold text-red-600 dark:text-red-400 break-words">
                                    {this.state.error?.toString() || 'Error desconocido'}
                                </code>
                            </div>

                            <div className="flex flex-col sm:flex-row gap-4 justify-center">
                                <button 
                                    onClick={() => window.location.reload()}
                                    className="px-8 py-4 bg-primary text-white font-bold rounded-2xl shadow-xl shadow-primary/25 hover:scale-[1.02] active:scale-[0.98] transition-all flex items-center justify-center gap-2"
                                >
                                    <span className="material-symbols-outlined text-lg">refresh</span>
                                    Recargar Aplicación
                                </button>
                                
                                <button 
                                    onClick={this.handleCopyReport}
                                    className={`px-8 py-4 font-bold rounded-2xl border transition-all flex items-center justify-center gap-2 ${
                                        this.state.copied 
                                        ? 'bg-emerald-50 dark:bg-emerald-500/10 border-emerald-200 dark:border-emerald-500/20 text-emerald-600 dark:text-emerald-400' 
                                        : 'bg-white dark:bg-white/5 border-slate-200 dark:border-white/10 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-white/10'
                                    }`}
                                >
                                    <span className="material-symbols-outlined text-lg">
                                        {this.state.copied ? 'check_circle' : 'content_copy'}
                                    </span>
                                    {this.state.copied ? '¡Reporte Copiado!' : 'Copiar Reporte Técnico'}
                                </button>
                            </div>

                            {/* Collapsible details for devs */}
                            <details className="mt-12 group">
                                <summary className="text-center list-none cursor-pointer">
                                    <span className="text-[10px] font-black uppercase tracking-widest text-slate-400 group-hover:text-primary transition-colors">
                                        Ver Detalles Técnicos
                                    </span>
                                </summary>
                                <div className="mt-6 p-6 bg-slate-900 rounded-2xl overflow-x-auto max-h-64 custom-scrollbar">
                                    <pre className="text-[11px] text-emerald-500 font-mono leading-relaxed">
                                        {this.state.error?.stack}
                                        {"\n\nComponent Stack:\n"}
                                        {this.state.errorInfo?.componentStack}
                                    </pre>
                                </div>
                            </details>
                        </div>
                        
                        <div className="px-8 py-4 bg-slate-50 dark:bg-black/20 border-t border-slate-100 dark:border-white/5 text-center">
                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                                TornadoCRM Core Engine · Error ID: {Math.random().toString(36).substring(7).toUpperCase()}
                            </p>
                        </div>
                    </div>
                </div>
            );
        }
        return this.props.children;
    }
}
